import { ReactComponent as SupportIcon } from '@assets/icons/support.svg';
import Box from '@mui/material/Box';
import { useLocation } from '@reach/router';
import { graphql } from 'gatsby';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
// @ts-ignore
import { Button, TextField } from '@lesmills-international/components';
import { RichText } from 'prismic-reactjs';
import { NavItem } from '../components/common/footer/Footer';
import Layout from '../components/common/layout';
import Spinner from '../components/common/spinner';
import { Wrapper } from '../components/signup/createAccount/style';
import { breakpoint } from '../themes/breakpoints';
import COLORS from '../themes/colors';

const Note = styled.div`
  text-align: left;
  line-height: 26px;
  color: ${(props) => props.theme.colors.secondaryText};
  margin-top: 20px;
  margin-bottom: 10px;
  font-size: 14px;
  font-family: Inter Regular;
  font-weight: 400;
`;

const Subtitle = styled.div`
  text-align: center;
  line-height: 26px;
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 10px;
  font-size: 14px;
  font-family: Inter Regular;
  font-weight: 400;
`;

type AlertSeverity = 'error' | 'success';

const AlertWrapper = styled.div<{ severity: AlertSeverity }>`
  padding: 16px 12px;
  border-radius: 6px;
  background-color: ${(props) => (props.severity === 'error' ? '#BF222F' : COLORS.green)};
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const AlertTitle = styled.p`
  font-family: Inter Regular;
  font-weight: 500;
  font-size: 16px;
  color: ${COLORS.white};
`;

const AlertText = styled.p`
  font-family: Inter Light;
  font-weight: 400;
  font-size: 14px;
  color: ${COLORS.white};
  margin-top: 10px;
`;

const Title = styled.h2`
  font-family: 'Inter Medium';
  color: ${(props) => props.theme.colors.darkGrey};
  font-size: 46px;
  font-weight: 600;
  align-self: center;
  font-size: 24px;
  line-height: 84px;
`;

const Text = styled.p`
  font-family: Inter Medium;
  font-size: 20px;
  line-height: 29px;
  margin-bottom: 1.5rem;
  ${breakpoint('md')`
        font-size: 24px;
        line-height: 36px;
    `}
`;

const CodeText = styled(Text)`
  font-size: 24px;
  font-weight: bold;
  text-align: center;
  ${breakpoint('md')`
        font-size: 32px;
    `}
`;

const ButtonWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '10px',
  width: '100%',
  marginTop: '20px',
  justifyContent: 'center',
}));

const Form = styled(Box)(() => ({
  '& .MuiTextField-root': { marginBottom: '15px', width: '320px' },
  maxWidth: '320px',
  textAlign: 'center',

  '& .MuiInputBase-input:focus': { boxShadow: 'none' },
}));

const DenyButton = styled.a`
  width: 100%;
  margin-top: 20px;
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.criticalRed};
  font-family: Inter Medium;
  align-self: center;
  text-align: center;
`;

interface AuthUrls {
  allow_url: string;
  deny_url: string;
}

/**
 * device-auth GET /auth/device answers 404 with one of these messages when the
 * code itself is the problem (expired, or never existed). Reloading can never
 * fix that, so these route to the "enter a new code" affordance instead of the
 * retry button (LPE-8445).
 */
const UNUSABLE_CODE_MESSAGES = ['User code has expired', 'User code not found'];

// Only the unusable-code copy is authored in Prismic (error_title /
// error_description already read "To get a new code, go to the start screen..."),
// so the transient-failure copy lives here.
const GENERIC_ERROR_COPY = {
  title: 'Something went wrong',
  description:
    "We couldn't load your authorization options. Please check your connection and try again.",
};

type ErrorKind = 'UNUSABLE_CODE' | 'GENERIC';

interface DeviceLoginProps {
  data: {
    prismicDeviceAuthPage: {
      data: {
        title: {
          text: string;
        };
        subtitle: string;
        helper_text: {
          richText: any;
        };
        accept_button_text: string;
        deny_button_text: string;
        error_title: string;
        error_description: string;
        try_again_button_text: string;
      };
    };
    prismicSignUpLayout: {
      data: {
        logo_white: {
          alt: string;
        };
        background_image: {
          alt: string;
          gatsbyImageData: object;
        };
        logo_black: {
          alt: string;
          gatsbyImageData: object;
        };
        body: { items: NavItem[] }[];
      };
    };
  };
}

const DeviceLogin: React.FC<DeviceLoginProps> = ({ data }) => {
  const { prismicDeviceAuthPage, prismicSignUpLayout } = data;

  const location = useLocation();
  const [deviceCode, setDeviceCode] = useState<string>('');
  const [inputCode, setInputCode] = useState<string>('');
  const [isManualInput, setIsManualInput] = useState<boolean>(false);
  const [authUrls, setAuthUrls] = useState<AuthUrls | null>(null);
  const [error, setError] = useState<ErrorKind | null>(null);

  const fetchAuthUrls = async (code: string) => {
    try {
      const response = await fetch(
        `${process.env.GATSBY_DEVICE_AUTH_API_URL}?userCode=${encodeURIComponent(code)}`
      );
      const body = (await response.json().catch(() => null)) as
        | (Partial<AuthUrls> & { message?: string })
        | null;

      if (!response.ok) {
        setError(
          UNUSABLE_CODE_MESSAGES.includes(body?.message ?? '') ? 'UNUSABLE_CODE' : 'GENERIC'
        );
        return;
      }

      // A 200 without usable URLs would leave the spinner up forever, so treat
      // it as a transient failure the member can retry.
      if (!body?.allow_url || !body?.deny_url) {
        setError('GENERIC');
        return;
      }

      setAuthUrls(body as AuthUrls);
    } catch (err) {
      setError('GENERIC');
    }
  };

  // An expired or unknown code can't be recovered by retrying it, so send the
  // member back to the manual form with a clean slate. history.replaceState
  // (rather than navigate) drops the stale userCode without re-triggering the
  // location effect below.
  const handleEnterNewCode = () => {
    setError(null);
    setAuthUrls(null);
    setDeviceCode('');
    setInputCode('');
    setIsManualInput(true);
    window.history?.replaceState?.(null, '', window.location?.pathname);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('userCode');
    if (!code) {
      setIsManualInput(true);
      return;
    }
    setDeviceCode(code);
    fetchAuthUrls(code);
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputCode) {
      setError(null);
      setDeviceCode(inputCode);
      setIsManualInput(false);
      await fetchAuthUrls(inputCode);
    }
  };

  return (
    <Layout
      data={prismicSignUpLayout?.data}
      currentStep={0}
      scripts={[]}
      lang="en-NZ"
      pathName="/verify"
      omitClose
      disableLogoLink
      title="Log In | LES MILLS+"
    >
      <Wrapper>
        <Title>{prismicDeviceAuthPage?.data?.title?.text}</Title>
        {error ? (
          <>
            <AlertWrapper severity="error">
              <div style={{ marginTop: '2px' }}>
                <SupportIcon color="white" width={17} height={17} />
              </div>
              <div>
                <AlertTitle>
                  {error === 'UNUSABLE_CODE'
                    ? prismicDeviceAuthPage?.data?.error_title
                    : GENERIC_ERROR_COPY.title}
                </AlertTitle>
                <AlertText>
                  {error === 'UNUSABLE_CODE'
                    ? prismicDeviceAuthPage?.data?.error_description
                    : GENERIC_ERROR_COPY.description}
                </AlertText>
              </div>
            </AlertWrapper>
            {error === 'UNUSABLE_CODE' ? (
              <Button
                type="button"
                id="enter-new-code-button"
                variant="contained"
                ctaButton
                style={{ width: '100%', marginTop: '20px' }}
                label="Enter a new code"
                onClick={handleEnterNewCode}
              />
            ) : (
              <Button
                type="button"
                id="try-again-button"
                variant="contained"
                ctaButton
                style={{ width: '100%', marginTop: '20px' }}
                label="Try again"
                onClick={() => {
                  // refresh the page
                  window.location.reload();
                }}
              />
            )}
          </>
        ) : isManualInput ? (
          <Form onSubmit={handleSubmit}>
            <TextField
              required
              data-testid="device-code-input"
              label="Enter device code"
              variant="filled"
              name="inputCode"
              type="text"
              id="inputCode"
              value={inputCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputCode(e.target.value)}
            />
            <Button
              id="submit-button"
              disabled={!inputCode}
              variant="contained"
              ctaButton
              style={{ width: '100%', marginTop: '20px' }}
              label="Continue"
              onClick={handleSubmit}
            />
            <Note>
              <RichText render={prismicDeviceAuthPage?.data?.helper_text?.richText} />
            </Note>
          </Form>
        ) : (
          <>
            <Subtitle>{prismicDeviceAuthPage?.data?.subtitle}</Subtitle>
            <CodeText>{deviceCode}</CodeText>
            {authUrls ? (
              <ButtonWrapper>
                <DenyButton id="deny-button" href={authUrls?.deny_url ?? ''}>
                  Deny
                </DenyButton>
                <Button
                  type="button"
                  id="allow-button"
                  variant="contained"
                  ctaButton
                  style={{ width: '100%', marginTop: '20px' }}
                  label="Accept"
                  as="a"
                  onClick={() => {
                    window.location.href = authUrls?.allow_url ?? '';
                  }}
                />
              </ButtonWrapper>
            ) : (
              <Spinner />
            )}
          </>
        )}
      </Wrapper>
    </Layout>
  );
};

export default DeviceLogin;

export const query = graphql`
  query ($lang: String) {
    prismicSignUpLayout(lang: { eq: $lang }) {
      _previewable
      data {
        logo_white {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        logo_black {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        background_image {
          alt
          gatsbyImageData
        }
        body {
          ... on PrismicSignUpLayoutDataBodyFooterNavigation {
            items {
              nav_label {
                text
              }
              nav_link {
                text
              }
            }
          }
        }
        body1 {
          ... on PrismicSignUpLayoutDataBody1Countries {
            items {
              country_code {
                text
              }
              country_name {
                text
              }
            }
          }
        }
        body2 {
          ... on PrismicSignUpLayoutDataBody2States {
            primary {
              country_name {
                text
              }
              country_code {
                text
              }
            }
            items {
              state_name {
                text
              }
              state_code {
                text
              }
              state_short_name {
                text
              }
            }
          }
        }
      }
    }
    prismicDeviceAuthPage(lang: { eq: $lang }) {
      _previewable
      lang
      data {
        title {
          text
        }
        continue_button_text {
          text
        }
        helper_text {
          richText
        }
        subtitle
        accept_button_text
        deny_button_text
        error_title
        error_description
        try_again_button_text
      }
    }
  }
`;
